// Ultimate Card Game (UCG) - Bot AI deck profile
//
// What KIND of deck is this? AdaptiveMemory.analyzeDeckThreats() used to
// answer with absolute counts (>= 2 flyers, >= 2 heavies, >= 3 cheap units),
// which a 40-card player deck reaches in every category: all six reference
// player decks of tools/ai-eval/playerDecks.mjs came out as "combined", and
// the counter-drafting built the same answer against every one of them
// (docs/AI_MASTERPLAN_2026-10.md, B20).
//
// A deck is described by the SHARE of its cards in a few categories, and a
// category counts as a trait of the deck only if it stands out against what a
// random legal deck of the same size looks like (a z-score against 2000
// random 40-card decks from the active pool, deterministic). A deck full of
// anti-air is nothing special - every deck has some - but a deck with three
// standard deviations more stealth than chance is a stealth deck.

import { getActiveCards, getCardByName } from '../../data/cards.js';
import { cardTags } from '../../data/aiDoctrines.js';
import { getCardHint } from '../../data/aiCardHints.js';
import { SeededRNG } from '../rng.js';

// Below this many cards the old absolute counts are what a deck is judged by
// (hand-made 10-15 card test decks, the bot's own 15-20 card decks).
export const LARGE_DECK_MIN = 30;
// A trait must stand out by this many standard deviations.
export const TRAIT_Z = 1.2;

export const SHARE_KEYS = ['air', 'antiAir', 'swarm', 'heavy', 'stealth', 'siege', 'splash', 'spell'];
// The traits that name a threat, and the threat class each one answers to in
// the counter matrix (js/data/aiDecks.js COUNTER_MATRIX).
export const TRAIT_THREAT = Object.freeze({ air: 'air', swarm: 'swarm', heavy: 'heavy', stealth: 'stealth', siege: 'siege_fortress' });

// Shares of a card list (names or cards) in each category, plus its average cost.
export function deckShares(cardList) {
  const counts = Object.fromEntries(SHARE_KEYS.map(k => [k, 0]));
  let n = 0;
  let cost = 0;
  for (const item of cardList) {
    const card = typeof item === 'string' ? getCardByName(item) : (item?.name ? getCardByName(item.name) || item : null);
    if (!card) continue;
    n++;
    cost += card.cost || 0;
    const tags = cardTags(card);
    const roles = getCardHint(card).roles;
    const isSpell = card.isSpell || card.type === 'Spell';
    const isUnit = !isSpell && !card.isModule && card.type !== 'Module';
    if (tags.has('air')) counts.air++;
    if (tags.has('antiAir')) counts.antiAir++;
    if (tags.has('cloak')) counts.stealth++;
    if (tags.has('splash')) counts.splash++;
    if (isSpell) counts.spell++;
    // Cheap bodies, and Fodder (eight Marines in one card) counts as a swarm in itself.
    if (isUnit && ((card.cost || 0) <= 9 || roles.includes('swarm'))) counts.swarm++;
    if (card.name === 'Fodder') counts.swarm += 2;
    if (isUnit && !card.isStructure && ((card.cost || 0) >= 30 || (card.hp || 0) >= 175)) counts.heavy++;
    if (roles.includes('siege') || roles.includes('outranger') || roles.includes('frontlineStructure')) counts.siege++;
  }
  const shares = {};
  for (const k of SHARE_KEYS) shares[k] = n ? counts[k] / n : 0;
  shares.avgCost = n ? cost / n : 0;
  shares.cards = n;
  return shares;
}

let baseline = null;

// Mean and spread of every share over 2000 random legal 40-card decks (at most
// 3 copies of a card) from the active roster. Computed once, on first use or idle warmup.
export function baselineStats() {
  if (baseline) return baseline;
  const pool = getActiveCards().map(c => c.name);
  const rng = new SeededRNG(5);
  const samples = [];
  for (let i = 0; i < 2000; i++) {
    const copies = {};
    const deck = [];
    while (deck.length < 40) {
      const name = rng.pick(pool);
      if ((copies[name] || 0) >= 3) continue;
      copies[name] = (copies[name] || 0) + 1;
      deck.push(name);
    }
    samples.push(deckShares(deck));
  }
  const stats = {};
  for (const k of [...SHARE_KEYS, 'avgCost']) {
    const mean = samples.reduce((a, s) => a + s[k], 0) / samples.length;
    const sd = Math.sqrt(samples.reduce((a, s) => a + (s[k] - mean) ** 2, 0) / samples.length) || 1;
    stats[k] = { mean, sd };
  }
  baseline = stats;
  return stats;
}

// Warmup hook: pre-computes baselineStats in the background during idle time / menu boot
// so there is zero latency spike when a bot first classifies a player deck in late game.
export function scheduleBaselineWarmup() {
  if (baseline) return;
  const run = () => {
    if (!baseline) {
      try {
        baselineStats();
      } catch (_) {}
    }
  };
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(run, { timeout: 3000 });
  } else if (typeof setTimeout === 'function') {
    setTimeout(run, 1000);
  }
}

// How far each share stands out from a random deck, in standard deviations.
export function deckZScores(cardList) {
  const shares = deckShares(cardList);
  const stats = baselineStats();
  const z = {};
  for (const k of [...SHARE_KEYS, 'avgCost']) z[k] = (shares[k] - stats[k].mean) / stats[k].sd;
  return { shares, z };
}

// The deck's threat identity: the traits that stand out, strongest first.
//   primary    the strongest trait's threat class, 'combined' when two or more
//              stand out about equally, 'balanced' when none does
//   secondary  the next trait's threat class (or null)
export function classifyLargeDeck(cardList) {
  const { shares, z } = deckZScores(cardList);
  const traits = Object.keys(TRAIT_THREAT)
    .map(k => ({ trait: k, z: z[k] }))
    .filter(t => t.z >= TRAIT_Z)
    .sort((a, b) => b.z - a.z);
  let primary = 'balanced';
  let secondary = null;
  let combined = false;
  if (traits.length >= 1) {
    primary = TRAIT_THREAT[traits[0].trait];
    if (traits.length >= 2) {
      secondary = TRAIT_THREAT[traits[1].trait];
      // Two traits within half a standard deviation of each other: no single answer.
      if (traits[0].z - traits[1].z < 0.5) { combined = true; primary = 'combined'; }
    }
  }
  return { primary, secondary, combined, shares, z, traits: traits.map(t => t.trait) };
}
